import { Panel, useConnection, useReactFlow, useViewport } from "@xyflow/react"
import PanelCard from "./PanelCard"
import { FieldEdit } from "@/components/form/fields/FieldEdit"
import { store } from "@/stores/stores"
import { useSnapshot } from "valtio"
import { ScenarioCanvasProps } from "../Scenario.canvas"
import { Text } from "@mantine/core"
import { useHistoryStateReturn } from "../useHistoryState"
import { cn } from "@/lib/utils"
import PanelButton from "./PanelButton"
import { Scenario, ScenarioNode } from "@/gen"
import ActionsSvg from "@/assets/images/actions.svg?react"
import { Link } from "react-router"
import { Button } from "@/components/ui/button"
import { Tooltip } from "@/components/ui/tooltip"
import { Separator } from "@/components/ui/separator"
import { toast } from "@/lib/toast"
import { ExecutionsSheet } from "./analytic/ExecutionsSheet"
import { useState } from "react"
import ScenarioSettingsMenu from "./ScenarioSettingsMenu"

interface PanelsProps extends ScenarioCanvasProps {
  onLayout: () => void
  handlePublish: (version_name: string) => Promise<Scenario | undefined>
  drafted: boolean
  setDrafted: (value: boolean) => void
  history: useHistoryStateReturn
  preparePublish: () => ScenarioNode[]
}

function Panels(props: PanelsProps) {
  const { zoomTo, setCenter, getNode } = useReactFlow()
  const { zoom } = useViewport()
  const connection = useConnection()
  const [runLoading, setRunLoading] = useState(false)

  const snap = useSnapshot(store)

  const hidePanels = snap.scenario.editing.draggingNode || connection.inProgress

  return (
    <>
      {snap.scenario.editing.draggingNode ||
        (connection.inProgress && (
          <Panel position="top-center" className={cn("animate-fade-down py-2 animate-delay-200 animate-duration-100")}>
            <PanelCard className="-ml-28 flex items-center gap-2 px-3 text-lg font-medium">
              {connection.inProgress ? (
                connection.toHandle?.type === "target" ? (
                  <>
                    <IconHugeiconsConnect className={"mx-1 size-6 text-emerald-500 [&_path]:stroke-[2.5]"} />
                    Drop to connect actions
                  </>
                ) : (
                  <>
                    <IconTablerDragDrop2 className="mx-1 size-6" />
                    Select the place to add the action
                  </>
                )
              ) : (
                <>
                  <IconTablerDragDrop2 className="mx-1 size-6" />
                  Drop to add action
                </>
              )}
            </PanelCard>
          </Panel>
        ))}
      <div className={cn("transition-all delay-100 duration-200 ease-in-out", hidePanels && "opacity-0")}>
        {/* Top left */}
        <Panel position="top-left">
          <PanelCard className="gap-0 pr-4 pl-1.5">
            <Link to="/scenarios">
              <PanelButton label="Go back">
                <IconMaterialSymbolsArrowBackRounded />
              </PanelButton>
              {/* <PanelButton label="Home">
                <LogoSvg className="size-7" />
              </PanelButton> */}
            </Link>
            <FieldEdit
              title="Edit scenario name"
              initialValue={props.scenario.name}
              onSubmit={(value) => snap.scenario.actions.update(props.scenario.id!, { name: value })}
            >
              <Tooltip tip="Click to rename">
                <Button variant="ghost" className="h-full text-[17px] px-2 font-medium">
                  {props.scenario.name}
                  {/* <IconHugeiconsEdit01 className="[&_path]:stroke-[2]" /> */}
                </Button>
              </Tooltip>
            </FieldEdit>
          </PanelCard>
        </Panel>

        {/* Bottom left */}
        <Panel position="bottom-left" className="flex gap-4">
          <PanelCard className="group h-[43px] px-1 py-2">
            <PanelButton
              label="Start point"
              tooltipPosition="top"
              onClick={() => {
                const node = getNode("start")
                setCenter(node?.position?.x || 0, node?.position?.y || 0, { duration: 200, zoom: 1 })
              }}
            >
              <IconHugeiconsHome04 />
            </PanelButton>
            <PanelButton label="Organize nodes" tooltipPosition="top" onClick={props.onLayout}>
              <IconHugeiconsWorkflowSquare06 />
            </PanelButton>
            <Separator orientation="vertical" className="mx-1.5" />

            {/* <Divider orientation="vertical" className="mx-1.5" /> */}
            {/* <PanelButton
              label="Zoom Out"
              tooltipPosition="top"
              onClick={() => zoomOut({ duration: 100 })}
              className={`
                hidden

                group-hover:block
              `}
            >
              <IconHugeiconsMinusSign />
            </PanelButton>
            <PanelButton
              label="Zoom In"
              tooltipPosition="top"
              onClick={() => zoomIn({ duration: 100 })}
              className={`
                hidden

                group-hover:block
              `}
            >
              <IconHugeiconsAdd01 />
            </PanelButton> */}
            <PanelButton
              label="Reset zoom"
              tooltipPosition="top"
              className="w-auto px-1 text-[13px] font-medium"
              onClick={() => zoomTo(1, { duration: 100 })}
            >
              {parseInt(zoom * 100 + "", 10)}%
            </PanelButton>
          </PanelCard>
          <PanelCard className="h-[43px] px-1 py-2">
            <PanelButton label="Undo" tooltipPosition="top" disabled={!props.history.canUndo} onClick={props.history.undo}>
              <IconOcticonUndo16 className="!size-5" />
            </PanelButton>
            {/* <Divider orientation="vertical" /> */}
            <Separator orientation="vertical" />
            <PanelButton label="Redo" tooltipPosition="top" disabled={!props.history.canRedo} onClick={props.history.redo}>
              <IconOcticonRedo16 className="!size-5" />
            </PanelButton>
          </PanelCard>
        </Panel>

        {/* Top right */}
        <Panel position="top-right" className="flex gap-4">
          <PanelCard>
            <PanelButton
              label="Add Action"
              onClick={() => {
                snap.scenario.actions.setSelectedNode({ node_type: "add-action" })
              }}
              className="!w-15 !py-0"
            >
              {/* <IconHugeiconsZap /> */}
              {/* <IconHugeiconsNodeAdd /> */}
              {/* <IconHugeiconsAddCircleHalfDot /> */}
              <ActionsSvg className="!w-auto !h-[46px]" />
            </PanelButton>
            <ExecutionsSheet scenario={props.scenario}>
              <PanelButton label="Analítico" className="!w-15 !py-0">
                <IconHugeiconsPresentationBarChart01 />
              </PanelButton>
            </ExecutionsSheet>

            <PanelButton label="Search" disabled>
              <IconHugeiconsSearchArea />
            </PanelButton>
            <PanelButton label="Version history" disabled>
              <IconHugeiconsClock02 />
            </PanelButton>
            <ScenarioSettingsMenu>
              <PanelButton label="Settings">
                <IconHugeiconsSettings02 />
              </PanelButton>
            </ScenarioSettingsMenu>
          </PanelCard>
          <PanelCard className="gap-2">
            <Button
              className="h-9"
              variant="secondary"
              type="button"
              loading={runLoading}
              onClick={() => {
                const nodeId = props.nodes.find((node) => node.data.node_type === "manual")?.id

                if (!nodeId) {
                  toast.error("No manual trigger found")
                  return
                }

                setRunLoading(true)
                const nodes = props.preparePublish()

                store.scenario.actions
                  .runScenario({
                    scenarioId: props.scenario.id,
                    nodeId,
                    version: { ...props.scenario, nodes },
                  })
                  .then((res) => {
                    toast.success("Scenario run successfully")
                  })
                  .finally(() => {
                    setRunLoading(false)
                  })
              }}
            >
              <IconMaterialSymbolsPlayArrowRounded className="[&_path]:stroke-[2.5]" />
              Run
            </Button>
            <FieldEdit
              title="Publish new version"
              inputProps={{
                placeholder: "Enter version name (optional)",
              }}
              before={
                <Text className="mb-6">This action will publish the selected version. Are you sure you want to publish this version?</Text>
              }
              confirmButtonProps={{
                children: "Publish",
              }}
              disabled={!props.drafted}
              onSubmit={props.handlePublish}
            >
              <Button className="h-9" disabled={!props.drafted} onClick={() => store.scenario.actions.setSelectedNode(null)}>
                Publish
              </Button>
            </FieldEdit>
          </PanelCard>
        </Panel>
      </div>
    </>
  )
}

export default Panels
